import {
  Icon,
  Button,
  ScrollArea,
  Input,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@elaniintc/goes-design-system";
import { useState, useEffect, useMemo } from "react";
import { cn } from "@/utils/theme";
import {
  useGetDataCenterDataById,
  useGetTableDataInfinite,
} from "@/queries/data-center/data-center.api";
import { useDebounceHook } from "@/hooks/use-debounce";

interface SelectOption {
  value: string;
  label: string;
  table?: string;
}

interface DataCenterDataSelectProps {
  name?: string;
  label?: string;
  required?: boolean;
  labelHelperText?: string;
  helperText?: string;
  placeholder?: string;
  value: { key: string; label: string } | string | null;
  onChange: (value: { key: string; label: string; table?: string }) => void;
  isDisabled?: boolean;
  group?: "down" | "left" | "right" | "up";
  database?: {
    table: string | null | undefined;
    column: string | null | undefined;
  };
  initOptions?: string[];
  enableSearch?: boolean;
  className?: string;
  wrapperClassName?: string;
}

export const DataCenterDataSelect = ({
  name,
  label,
  required = false,
  labelHelperText,
  helperText,
  placeholder = "Selecciona una opción",
  value,
  onChange,
  isDisabled = false,
  group,
  database,
  initOptions,
  enableSearch = false,
  className,
  wrapperClassName,
}: DataCenterDataSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [options, setOptions] = useState<SelectOption[]>([]);
  const debouncedSearch = useDebounceHook(searchString, 300);
  const [fetchSingleElement, setFetchSingleElement] = useState(false);
  const [internalSelectedOption, setinternalSelectedOption] =
    useState<SelectOption | null>(null);
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingInfinite,
  } = useGetTableDataInfinite({
    name: database?.table || "",
    search: debouncedSearch,
    per_page: 10,
  });
  const { data: singleData } = useGetDataCenterDataById(
    database?.table || "",
    internalSelectedOption?.value || "",
    !!database && !!internalSelectedOption && fetchSingleElement,
  );

  // Transform infinite query data to options
  const databaseOptions = useMemo(() => {
    if (!database?.table || !database?.column || !infiniteData) return [];

    const column_name = database.column;
    const table_name = database.table;

    const dbOptions =
      infiniteData.pages.flatMap((page) =>
        (page.data?.data || []).map(
          (item: { [key: string]: string | number }) => ({
            value: item.id as string,
            label: item[column_name] as string,
            table: table_name,
          }),
        ),
      ) || [];

    if (singleData && singleData.data.length > 0) {
      const element = singleData.data[0];
      const exists = dbOptions.find((opt) => opt.value === element.id);
      if (!exists) {
        dbOptions.push({
          value: element.id,
          label: element[column_name],
          table: table_name,
        });
      }
    }
    return dbOptions;
  }, [infiniteData, database?.table, database?.column, singleData]);

  // Set the component source options based on database or initial options
  useEffect(() => {
    if (database?.table) {
      // Always update options from database, even if empty
      setOptions(databaseOptions);
    } else if (initOptions && !database?.table) {
      setOptions(
        initOptions.filter(Boolean).map((option) => ({
          value: option,
          label: option,
        })),
      );
    }
  }, [databaseOptions, initOptions, database?.table]);

  // Format the display label based on current value and options
  useEffect(() => {
    setFetchSingleElement(false);
    if (!options || options.length === 0) return;
    if (!value) {
      setinternalSelectedOption(null);
      return;
    }
    if (typeof value === "object" && "key" in value && "label" in value) {
      setinternalSelectedOption({
        value: value.key,
        label: value.label,
      });
      return;
    }
    // When value is a string
    // search for the option with matching value (UUID)
    const foundOption = options.find((opt) => opt.value === value);
    if (foundOption) {
      setinternalSelectedOption(foundOption);
    } else {
      // Fallback: try to find by label (handles legacy registries where
      // the display text was stored as relation_id instead of the UUID)
      const foundByLabel = options.find((opt) => opt.label === value);
      if (foundByLabel) {
        setinternalSelectedOption(foundByLabel);
        // Don't set fetchSingleElement — we resolved it locally
      } else {
        setinternalSelectedOption({
          value: value,
          label: "",
        });
        setFetchSingleElement(true);
      }
    }
  }, [value, options, database?.table]);

  // Filter local options based on search string
  const filteredOptions = useMemo(() => {
    // For database-driven selects, options are already filtered by API
    if (database?.table) {
      return options;
    }
    // For local options, filter client-side
    if (!searchString.trim()) {
      return options;
    }
    const search = searchString.toLowerCase();
    return options.filter((opt) => opt.label.toLowerCase().includes(search));
  }, [options, searchString, database?.table]);

  const selectElement = (option: { key: string; label: string }) => {
    onChange(option);
    setOpen(false);
  };

  const SearchBox = enableSearch ? (
    <div className="border-b border-neutral-quiet p-3 relative ">
      <Icon
        type="far"
        name="search"
        className="absolute left-5 top-1/2 transform -translate-y-1/2 text-body"
      />
      <Input
        variant="sm"
        className="text-sm w-full font-normal flex-auto pl-9"
        placeholder={"Buscar..."}
        onChange={(e) => setSearchString(e.target.value)}
        value={searchString}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </div>
  ) : null;

  const TriggerButton = (
    <Button
      group={group}
      variant="tertiary"
      disabled={isDisabled}
      name={name}
      className={cn(
        "w-full h-full justify-between group items-center min-h-10 px-2 relative",
        className,
      )}
    >
      <p
        className={cn(
          "truncate font-normal pr-5",
          internalSelectedOption ? "text-body" : "text-body-quiet",
        )}
      >
        {internalSelectedOption?.label || placeholder}
      </p>

      <Icon
        type="far"
        name="angle-down"
        className="group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body absolute right-3 top-1/2 transform -translate-y-1/2"
      />
    </Button>
  );

  const ListView = (
    <ScrollArea
      className={cn(
        "max-h-60 max-w-(--radix-dropdown-menu-trigger-width)",
        database ? "h-60" : "",
      )}
    >
      <div className="p-1">
        {isLoadingInfinite ? (
          <div className="flex items-center justify-center py-6">
            <Icon
              type="far"
              name="spinner-third"
              className="animate-spin text-body"
            />
          </div>
        ) : filteredOptions.length > 0 ? (
          filteredOptions.map(({ label: optLabel, value: optValue }) => (
            <div
              key={optValue}
              onClick={() => selectElement({ key: optValue, label: optLabel })}
              className={cn(
                "flex items-center gap-2 rounded-lg text-body px-3 py-2 cursor-pointer",
                internalSelectedOption?.value === optValue
                  ? "bg-surface-action-accent"
                  : "hover:bg-neutral-object-accent-silent",
              )}
            >
              <p className="truncate font-normal pr-2 flex-1">{optLabel}</p>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center min-h-40 px-4">
            <p className="text-body-quiet text-base font-normal text-center">
              {searchString.trim().length > 0
                ? "No se encontraron resultados"
                : "No hay opciones disponibles"}
            </p>
          </div>
        )}
        {hasNextPage && (
          <div className="px-2 py-1">
            <Button
              variant="ghost-blue"
              size="sm"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? (
                <>
                  <Icon
                    name="spinner-third"
                    type="fas"
                    className="animate-spin"
                  />
                  Cargando...
                </>
              ) : (
                <>
                  Cargar más
                  <Icon name="add" type="fas" />
                </>
              )}
            </Button>
          </div>
        )}
      </div>
    </ScrollArea>
  );

  return (
    <div className={cn("flex flex-col gap-1 flex-1 min-w-0", wrapperClassName)}>
      {label && (
        <div className="flex w-full justify-between items-center">
          <label className="font-semibold text-body-loud text-sm leading-2xl">
            {label}
            {required && <span className="text-sm text-danger ml-1">*</span>}
          </label>
          {labelHelperText && (
            <p className="text-body-quiet text-sm">{labelHelperText}</p>
          )}
        </div>
      )}
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>{TriggerButton}</DropdownMenuTrigger>
        <DropdownMenuContent
          className={cn(
            "flex flex-col rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl w-(--radix-dropdown-menu-trigger-width)",
          )}
          side="bottom"
          align="start"
        >
          {SearchBox}
          {ListView}
        </DropdownMenuContent>
      </DropdownMenu>
      {helperText && (
        <p className="text-xs text-body-quiet mt-1">{helperText}</p>
      )}
    </div>
  );
};
